import { IconCopy, IconExternalLink, IconQrcode, IconShare3 } from "@tabler/icons-react";
import { QRCodeCanvas } from "qrcode.react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { HomeData } from "../use-home-data";

type SubscriptionCardProps = {
  businessType: "restaurant" | "supermarket";
  subscription: HomeData["subscription"];
  organizationSlug: string | null;
};

const QR_SIZE = 220;

export function SubscriptionCard({
  businessType,
  subscription,
  organizationSlug,
}: SubscriptionCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }),
    [i18n.language],
  );

  const publicUrl = organizationSlug ? `${window.location.origin}/m/${organizationSlug}` : null;
  const isSupermarket = businessType === "supermarket";
  const shareLabel = isSupermarket
    ? t("dashboard.subscription.sharePublicCatalog", {
        defaultValue: "Compartir catálogo público",
      })
    : t("dashboard.subscription.sharePublicMenu");
  const shareDescription = isSupermarket
    ? t("dashboard.subscription.shareCatalogDescription", {
        defaultValue: "Los clientes pueden abrir este enlace para ver tu catálogo.",
      })
    : t("dashboard.subscription.shareDescription");

  const status = subscription?.status ?? "inactive";
  const renewal = subscription?.currentPeriodEnd
    ? dateFormatter.format(new Date(subscription.currentPeriodEnd))
    : null;

  const badgeVariant: "default" | "outline" | "secondary" | "destructive" =
    status === "active"
      ? "default"
      : status === "past_due" || status === "canceled"
        ? "destructive"
        : "secondary";

  const handleCopy = async () => {
    if (!publicUrl) {
      return;
    }
    await navigator.clipboard.writeText(publicUrl);
    toast.success(t("dashboard.subscription.linkCopied"));
  };

  return (
    <Card className="@container/card flex flex-col">
      <CardHeader>
        <CardDescription>{t("dashboard.subscription.title")}</CardDescription>
        <CardTitle className="flex items-center gap-2 capitalize">
          {subscription?.productName ?? t("dashboard.subscription.noActivePlan")}
          <Badge className="capitalize" variant={badgeVariant}>
            {t(`dashboard.subscription.status.${status}`)}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {renewal ? (
          <div className="text-muted-foreground text-sm">
            {subscription?.cancelAtPeriodEnd
              ? t("dashboard.subscription.endsOn", { date: renewal })
              : t("dashboard.subscription.renewsOn", { date: renewal })}
          </div>
        ) : (
          <div className="text-muted-foreground text-sm">
            {t("dashboard.subscription.manageDescription")}
          </div>
        )}
        <div className="mt-auto flex flex-col gap-2">
          <Button
            className="w-full justify-start"
            onClick={() => navigate("/billing")}
            type="button"
            variant="outline"
          >
            <IconExternalLink className="size-4" />
            {t("dashboard.subscription.manage")}
          </Button>
          <Dialog onOpenChange={setShareOpen} open={shareOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full justify-start"
                disabled={!publicUrl}
                type="button"
                variant="outline"
              >
                <IconShare3 className="size-4" />
                {shareLabel}
              </Button>
            </DialogTrigger>
            {publicUrl && (
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <IconQrcode className="size-5" />
                    {shareLabel}
                  </DialogTitle>
                  <DialogDescription>{shareDescription}</DialogDescription>
                </DialogHeader>
                <div className="flex justify-center rounded-md border bg-white p-4 dark:bg-slate-100">
                  <QRCodeCanvas size={QR_SIZE} value={publicUrl} />
                </div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={publicUrl} />
                  <Button onClick={handleCopy} size="icon" type="button" variant="outline">
                    <IconCopy className="size-4" />
                  </Button>
                </div>
              </DialogContent>
            )}
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}
